<script setup>
import SvgIcon from "./SvgIcon.vue";

defineProps({
  type: {
    type: String,
    default: "info",
    validator: (v) => ["note", "info", "warning", "error", "success"].includes(v),
  },
});

const iconMap = {
  // Quiet, in-place context — states a fact about what is on screen.
  note: "sticker/exclamation-mark",
  // Guidance the reader should notice: what to do next, or what a screen means.
  info: "system/help",
  warning: "system/alert-triangle",
  error: "system/alert-triangle",
  success: "system/checkmark-circled-filled",
};
</script>

<template>
  <aside class="box" :class="type" role="note">
    <SvgIcon :name="iconMap[type]" class="icon" width="16" height="16" />
    <div class="content">
      <slot />
    </div>
  </aside>
</template>

<style lang="scss" scoped>
@use "@/colors";
@use "@/styles/mixins/typography";

.box {
  @include typography.small("bold");

  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: 1.5em auto;
  column-gap: 14px;
  padding: 12px;
  border-radius: 8px;
  line-height: 1.5;
}

.icon {
  grid-column: 1;
  grid-row: 1;
  place-self: center center;
}

.content {
  grid-column: 2;
  grid-row: 1 / -1;
  min-width: 0;
}

.note {
  background-color: colors.$background-grey-light;
  color: colors.$text-subtle-color;

  .icon {
    color: colors.$text-color;
  }
}

.info {
  background-color: colors.$blue-light;
  color: colors.$text-color;

  .icon {
    color: colors.$main;
  }
}

.warning {
  background-color: colors.$orange-light;
  color: colors.$text-color;

  .icon {
    color: colors.$brand-warning;
  }
}

.error {
  background-color: colors.$red-light;
  color: colors.$text-color;

  .icon {
    color: colors.$brand-danger;
  }
}

.success {
  background-color: colors.$green-light;
  color: colors.$text-color;

  .icon {
    color: colors.$brand-success;
  }
}
</style>
